/**
 * Progression du défilement à travers un élément, de 0 à 1.
 *
 * Même machinerie que `useParallax` (boucle `requestAnimationFrame`, lissage exponentiel,
 * écriture impérative d'une variable CSS pour ne pas re-rendre à chaque image) mais une
 * grandeur différente : non pas un décalage en pixels, mais **où en est la lecture** dans
 * l'élément. La valeur est calée sur le milieu de la fenêtre — 0 quand le haut de
 * l'élément y arrive, 1 quand son bas y passe — ce qui fait que tout ce qui suit cette
 * progression semble accroché à l'endroit qu'on est en train de lire.
 *
 * `milestones` découpe le trajet en jalons régulièrement espacés et renvoie combien ont
 * été franchis : de quoi allumer des repères au passage, sans recalculer leur position.
 */

import { useEffect, useRef, useState } from 'react';

/** Part de l'écart comblée à chaque image — même valeur que `useParallax`. */
const SMOOTHING = 0.08;

export interface ScrollProgress<T extends HTMLElement> {
  ref: React.RefObject<T | null>;
  /** Nombre de jalons déjà franchis, de 0 à `milestones`. */
  reached: number;
}

export function useScrollProgress<T extends HTMLElement>(milestones = 0): ScrollProgress<T> {
  const ref = useRef<T>(null);
  const [reached, setReached] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Mouvement refusé : on affiche l'état d'arrivée plutôt que l'état de départ. Un
    // trajet figé à zéro laisserait tous les repères éteints, ce qui se lit comme une
    // page inachevée — alors que l'état final ne perd aucune information.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setReached(milestones);
      return;
    }

    let current = 0;
    let frame = 0;
    let lastReached = 0;

    const target = (): number => {
      const rect = node.getBoundingClientRect();
      if (rect.height === 0) return 0;
      const progress = (window.innerHeight / 2 - rect.top) / rect.height;
      return Math.min(Math.max(progress, 0), 1);
    };

    const tick = (): void => {
      current += (target() - current) * SMOOTHING;
      node.style.setProperty('--cable-progress', current.toFixed(4));

      // Un jalon est franchi quand la progression dépasse son milieu : le repère s'allume
      // au moment où le paquet passe dessus, pas quand il entre dans sa zone.
      const next = Math.min(milestones, Math.max(0, Math.floor(current * milestones + 0.5)));
      if (next !== lastReached) {
        lastReached = next;
        setReached(next);
      }

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [milestones]);

  return { ref, reached };
}
